import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AccountResponse } from '@shared/api';
import { AmountUtils, cn, CurrencyUtils, PaletteUtils } from '@shared/utils';
import { Icon, Text } from '@shared/widgets';
import ChevronIcon from '@assets/icons/chevron.svg';

interface AccountsListProps {
  className?: string;
  accounts: AccountResponse[];
}

export const AccountsList = (props: AccountsListProps) => {
  const [options, setOptions] = useState<boolean>(true);

  const { t } = useTranslation('accounts');

  return (
    <section className={cn(PaletteUtils.isVcfcPalette && 'rounded-lg bg-primary p-5 pt-0', props.className)}>
      <div className='flex items-center justify-between py-4'>
        <Text weight={PaletteUtils.getPaletteParam('normal', 'medium')} size='xs' color={PaletteUtils.getPaletteParam(undefined, 'secondary-light')}>
          {t('payment.tile.otherCurrencies.title')}
        </Text>
        <div className='flex gap-1'>
          {props.accounts.map((account) => (
            <Text
              key={account.id}
              className={cn('flex h-5 w-5 items-center justify-center rounded border', PaletteUtils.isVcfcPalette && 'bg-white')}
              weight='semibold'
              size='xs'
              color='light-gray'
            >
              {CurrencyUtils.getSymbolByCode(account.currency)}
            </Text>
          ))}
          <Icon
            className={cn(
              'ml-2 flex flex-none items-center justify-center text-center text-xl text-slate transition-transform duration-200 dark:text-gray',
              options ? 'rotate-90' : '-rotate-90',
            )}
            icon={<ChevronIcon className={cn(PaletteUtils.isVcfcPalette && 'text-white')} />}
            onClick={(e) => (e.preventDefault(), e.stopPropagation(), setOptions((prev) => !prev))}
          />
        </div>
      </div>
      {options && (
        <div className='flex flex-col gap-2'>
          {props.accounts.map((account) => (
            <div
              key={account.id}
              className={cn('flex items-center gap-3 bg-ice px-3 py-2 dark:bg-slate', PaletteUtils.isVcfcPalette && 'bg-dark-graphite')}
            >
              <div
                className={cn(
                  'flex items-center justify-center gap-2 rounded border border-primary px-2',
                  PaletteUtils.isVcfcPalette && 'bg-white/70 px-3 py-2',
                  PaletteUtils.isPersonalPalette && 'bg-primary-700',
                )}
              >
                <Text uppercase color='primary'>
                  {CurrencyUtils.getSymbolByCode(account.currency)}
                </Text>
                <Text className='mt-0.5' uppercase weight='semibold' size='xs' color='primary'>
                  {t('accountType.type.sub', { ns: 'common' })}
                </Text>
              </div>
              <div>
                <Text size='xs' color={PaletteUtils.getPaletteParam(undefined, 'secondary-light')}>
                  {t('payment.tile.otherCurrencies.fields.accountBalance')}
                </Text>
                <Text weight='semibold' size='xs' color={PaletteUtils.getPaletteParam(undefined, 'white')}>
                  {AmountUtils.transformToAmountWithCurrency(account.availableBalance ?? 0, account.currency)}
                </Text>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
